/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useRef, useState } from 'react';
import { useClickAway } from 'react-use';
import styled from 'styled-components';
import { formatMinutesToRead } from 'utils/helpers';
import i18n, { SettingsType } from 'utils/i18n';
import { isUnitCompleted, useUserData } from 'utils/user';
import Button from './button';

export type UnitsPickerProps = {
  units: Unit[];
  parentTitle: string;
  currentUnitId: string;
};

export const UnitsPicker: React.FC<UnitsPickerProps> = ({ units, currentUnitId, parentTitle }) => {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { dictionary } = i18n[(router.locale as AcceptedLocales) || 'en-US'] as SettingsType;
  const { achievements } = useUserData();

  useClickAway(ref, () => {
    setOpen(false);
  });

  if (!units.length) return null;

  const currentUnitIndex = units.findIndex((item) => item.meta.id === currentUnitId);
  const prevUnit = units[currentUnitIndex - 1];
  const nextUnit = units[currentUnitIndex + 1];
  const completed = isUnitCompleted(achievements, units[currentUnitIndex]);

  return (
    <StyledUnitsPicker>
      {/* Prev button */}
      {prevUnit ? (
        <Button
          linkProps={{
            href: prevUnit.meta.path,
          }}
          alignIcon="left"
          iconName="chevron-left"
          mode="transparent"
          className="prev-btn"
        >
          {dictionary.previous}
        </Button>
      ) : (
        <div></div>
      )}
      <div className="popup-wrapper" ref={ref}>
        {/* Button */}
        <Button
          alignIcon="right"
          onClick={() => setOpen(!open)}
          iconName={open ? 'chevron-up' : 'chevron-down'}
          mode="transparent"
          size="small"
        >
          {dictionary.unit} {currentUnitIndex + 1} {dictionary.of} {units.length}{' '}
        </Button>
        {/* Popup */}
        {open && (
          <div className="unit-popup">
            <h2>{parentTitle}</h2>
            <ul>
              {units.map((item) => {
                const completed = !!achievements.find(
                  (ach) =>
                    ach.contentId === item.meta.id &&
                    ach.type === (item.meta.type === 'Quiz' ? 'QuizComplete' : 'UnitComplete')
                );
                return (
                  <li
                    key={item.meta.id}
                    className={item.meta.id === currentUnitId ? 'current' : ''}
                    onClick={() => setOpen(false)}
                  >
                    <Link href={item.meta.path}>
                      <span>{item.title}</span>
                      <div>
                        {completed ? (
                          <span className="checkmark-completed docon docon-check"></span>
                        ) : (
                          <span>{formatMinutesToRead(item.meta.readingTime, dictionary)}</span>
                        )}
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
      {/* Next button */}
      {nextUnit && completed ? (
        <Button
          onClick={() => router.push(nextUnit.meta.path)}
          iconName="chevron-right"
          alignIcon="right"
          mode="transparent"
          className="next-btn"
        >
          {dictionary.next}
        </Button>
      ) : (
        <div></div>
      )}
    </StyledUnitsPicker>
  );
};

export const StyledUnitsPicker = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  align-items: center;

  .btn:hover {
    border-color: var(--primary-hover);
    color: var(--primary-hover);
    text-decoration: none;
  }

  .prev-btn {
    justify-self: start;
  }
  .next-btn {
    justify-self: end;
  }

  .popup-wrapper {
    position: relative;
    justify-self: center;
  }

  .unit-popup {
    margin-top: 12px;
    left: 50%;
    max-width: 90vw;
    transform: translateX(-50%);
    white-space: normal;
    background-color: var(--body-background);
    border: 1px solid var(--border);
    box-shadow: 0 6.4px 14.4px 0 var(--box-shadow-medium), 0 1.2px 3.6px 0 var(--box-shadow-light);
    position: absolute;
    top: 100%;
    /* white-space: nowrap; */
    width: auto;
    z-index: 1000;
    width: 450px;

    &:before {
      content: '';
      position: absolute;
      bottom: 100%;
      left: 50%;
      margin-left: -8px;
      border-width: 8px;
      border-style: solid;
      border-color: transparent;
      border-bottom-color: var(--border);
    }
    &:after {
      content: '';
      position: absolute;
      bottom: 100%;
      left: 50%;
      margin-left: -7px;
      border-width: 7px;
      border-style: solid;
      border-color: transparent;
      border-bottom-color: var(--body-background);
    }

    h2 {
      font-size: 1rem;
      padding: 1rem 1.25rem 0.5rem;
      text-align: center;
      margin: 0;
    }

    ul {
      list-style-type: none;
      color: var(--text-subtle);
      font-size: 0.875rem;
      margin: 0;
      display: flex;
      flex-direction: column;

      li {
        border-left: 3px solid var(--border);
        display: flex;
        padding: 0;
        width: 100%;
        justify-content: space-between;

        &:hover {
          background-color: var(--body-background-medium);
          border-left-color: var(--primary-base);
        }

        &.current {
          background-color: var(--body-background-medium);
          border: 1px solid var(--border-white-high-contrast);
          border-left: 3px solid var(--border);
          border-left-color: var(--primary-base);
          font-weight: 600;
        }

        a {
          display: flex;
          justify-content: space-between;
          padding: 0.5rem 1.25rem;
          text-align: left;
          width: 100%;
        }

        .checkmark-completed {
          color: var(--success-base) !important;
        }
      }
    }
  }
`;

export default UnitsPicker;
